import React, { useEffect } from "react";
import { isBoolean } from "lodash";
import styled from "styled-components";
import {
  Color,
  Elevation,
  ZIndexes,
  background,
  borderRadius,
  content,
  cover,
  transparentize,
} from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { useStickyElement } from "@lkf/hooks";
import Icon from "@ui-kit/icon";
import Tooltip from "@ui-kit/tooltip";
import SlidingStrip from "@ui-kit/utils/sliding-strip";
import { tr } from "@radarly/i18n";

interface IProps {
  children: React.ReactNode;
  disabled?: boolean;
  sticky?: boolean;
  isBelowBanner?: boolean;
  locale?: Locale;
  disabledTooltipLabel?: string;
  isVisibleAndDisabled?: boolean;
}

const Root = styled.div`
  position: relative;
  width: 100%;
`;

const FilterBar = styled(SlidingStrip)`
  background-color: ${background};
  border-top: 1px solid ${(props) => content(props, "tertiary")};
  height: 71px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: ${transparentize(0.8, Color.Black)} 0 1px 2px 0;
  position: initial;
`;

const Overlay = styled.div`
  ${cover()};
  background-color: ${Color.Solitude}; /* TODO: change this after themes refactoring */
  z-index: ${ZIndexes.Overlay};
  opacity: 0.5;
`;

const Handle = styled.div<{ side: "left" | "right" }>`
  background-color: ${background};
  box-shadow: ${Elevation.Elevation_2};
  height: 72px;
  width: 36px;
  ${(props) => borderRadius(props.side, 36)};
  padding-left: ${({ side }) => side === "left" && "10px"};
  padding-right: ${({ side }) => side === "right" && "10px"};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Filterbar = ({
  children,
  disabled,
  sticky,
  isBelowBanner,
  locale,
  disabledTooltipLabel,
  isVisibleAndDisabled,
}: IProps) => {
  const stickyBar = useStickyElement({
    stickyBitStickyOffset: isBelowBanner ? 50 : 0,
  });

  useEffect(() => {
    if (stickyBar.current) {
      // Change filter bar's z-index to make it overlay the rest of the page
      stickyBar.current.style.zIndex = ZIndexes.AboveContent.toString();
    }
  }, [stickyBar]);

  return (
    <Root
      ref={disabled ? null : isBoolean(sticky) && !sticky ? null : stickyBar}
    >
      {!!disabled && (
        <Tooltip
          tooltip={
            disabledTooltipLabel || tr(locale, "FILTERBAR::EDITION_DISABLED")
          }
        >
          <Overlay />
        </Tooltip>
      )}
      {isVisibleAndDisabled ? (
        <Tooltip
          tooltip={
            disabledTooltipLabel || tr(locale, "FILTERBAR::EDITION_DISABLED")
          }
        >
          <div style={{ pointerEvents: "none", opacity: 0.7 }}>
            <FilterBar
              leftHandle={
                <Handle side="right">
                  <Icon glyph="chevron-left" />
                </Handle>
              }
              rightHandle={
                <Handle side="left">
                  <Icon glyph="chevron-right" />
                </Handle>
              }
            >
              {children}
            </FilterBar>
          </div>
        </Tooltip>
      ) : (
        <FilterBar
          leftHandle={
            <Handle side="right">
              <Icon glyph="chevron-left" />
            </Handle>
          }
          rightHandle={
            <Handle side="left">
              <Icon glyph="chevron-right" />
            </Handle>
          }
        >
          {children}
        </FilterBar>
      )}
    </Root>
  );
};

export default Filterbar;
